import {ChangeDetectorRef, Component, HostListener} from "@angular/core";
import {NavigationService} from "../../orchestration/navigation.service";
import {SessionModel} from "../../orchestration/session.model";
import {DatePipe} from "@angular/common";
import {ButtonProminence} from "../../buttons/button/button";
import {Page} from "../../orchestration/page";
import {OrderModel} from "../../order/order.model";
import {OrderingContextModel} from "../ordering-context.model";
import {MessageLevel} from "../../notification/message/message";
import {NavigationParams} from "../../orchestration/navigation-params";
import {
    AvailableOrderTimeData,
    AvailableOrderTimesGroupData,
    AvailableOrderTimesResponseData
} from "../../order/available-order-times.service";

@Component({
    selector: "woa-order-time-selection-page",
    templateUrl: "./order-time-selection-page.component.html",
    providers: [{provide: Page, useExisting: OrderTimeSelectionPageComponent}]
})
export class OrderTimeSelectionPageComponent extends Page {
    static readonly MAX_DAYS_ORDER_LATER: number = 1;

    ButtonProminence = ButtonProminence;
    MessageLevel = MessageLevel;

    showAsap: boolean;
    showDateSelect: boolean;
    showTimeSelect: boolean;
    showEarliestDate: boolean;
    orderDate: Date = undefined;
    orderTime: Date = undefined;
    orderTimeIsCarryOver: boolean = false;
    earliestLocalDate: string;
    earliestDate: Date;
    formattedEarliestLocalDate: string;
    availableDates: Date[] = [];
    availableTimes: AvailableOrderTimesResponseData;
    loadingTimes: boolean = true;

    constructor(navigationParams: NavigationParams,
                private navigationService: NavigationService,
                private changeDetectorRef: ChangeDetectorRef,
                public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                private orderModel: OrderModel) {
        super();
        this.showAsap = orderingContextModel.canShowAsapSelectionTime && !navigationParams.get("hideAsap") && !(this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.closed);
        if (this.orderingContextModel.expectedCompletion) {
            this.orderDate = new Date(this.orderingContextModel.expectedCompletion);
            this.orderDate.setHours(0, 0, 0, 0);
            this.orderTime = new Date(this.orderingContextModel.expectedCompletion);
        }

        this.earliestLocalDate = this.orderModel.initiatedOrder ? this.orderModel.initiatedOrder.earliestLocalDate : undefined;
        if (this.earliestLocalDate) {
            this.earliestDate = this.parseDate(this.earliestLocalDate);
            this.formattedEarliestLocalDate = this.formatDate(this.earliestDate);
        }

        this.getAvailableDates();
        // noinspection JSIgnoredPromiseFromCall
        this.getAvailableTimes();
    }

    get showStoreClosed(): boolean {
        return !this.showAsap && this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.closed;
    }

    get canOrderLater(): boolean {
        return this.orderingContextModel.currentStore ? this.orderingContextModel.currentStore.orderAheadLaterStore || this.showEarliestDate : this.orderingContextModel.hasAnyStoresWithOrderLater();
    }

    get canOrderLaterTime(): boolean {
        return this.orderingContextModel.currentStore ? this.orderingContextModel.currentStore.orderAheadLaterTimeStore : this.orderingContextModel.hasAnyStoresWithOrderLaterTime();
    }

    get timesAvailable(): boolean {
        return !this.loadingTimes && this.availableTimes && this.availableTimes.specificTimeGroups.length > 0;
    }

    get noTimesMessage(): string {
        return this.isToday(this.orderDate) ? (this.orderingContextModel.currentStore ? "woa.message.order-time.noTimesTodayForStore" : "woa.message.order-time.noTimesToday")
            : (this.orderingContextModel.currentStore ? "woa.message.order-time.noTimesForStore" : "woa.message.order-time.noTimes");
    }

    getAvailableDates(): void {
        if (this.canOrderLater) {

            // Iterate the dates from today until the order ahead limit
            const maxDaysOrderLater: number = this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.maxDaysOrderLater ? this.orderingContextModel.currentStore.maxDaysOrderLater
                : this.sessionModel.currentApp.maxDaysOrderLater || OrderTimeSelectionPageComponent.MAX_DAYS_ORDER_LATER;
            let date: Date = this.sessionModel.now;
            date.setHours(0, 0, 0, 0);
            for (let i = 0; i <= maxDaysOrderLater; i++) {
                const localDate: string = new DatePipe("en-US").transform(date, "yyyy-MM-dd");

                // Only show available dates on or after the earliest available date
                if (!this.earliestLocalDate || localDate >= this.earliestLocalDate) {
                    this.availableDates.push(date);
                    if (!this.orderDate) {
                        this.orderDate = date;
                    }
                } else {

                    // If today's date is not available, then we can't choose "ASAP" and we must have a product with a lead time.
                    if (i == 0) {
                        this.showAsap = false;
                        this.showEarliestDate = true;
                    }

                    // If the currently selected date is not available, deselect it
                    if (this.orderDate && this.orderDate.getTime() === date.getTime()) {
                        this.orderDate = undefined;
                    }
                }

                date = new Date(date.getTime());
                date.setDate(date.getDate() + 1);
            }
        } else if (!this.isToday(this.orderDate)) {

            // If we can't order ahead and the currently selected date is in the future, default it to today.
            this.orderDate = this.sessionModel.now;
        }

        // If we have found no available dates, then either the earliest available date falls outside the normal order ahead limit due to a long product lead time,
        // or order ahead is not available. Either way, we want to offer the earliest date as the sole option.
        if (!this.availableDates.length && this.earliestDate) {
            this.availableDates.push(this.earliestDate);
            this.orderDate = this.earliestDate;

            // The server returns an "earliest date" of today if there are no product lead times,
            // but we don't want to display it in a message in that case.
            if (!this.isToday(this.earliestDate)) {
                this.showEarliestDate = true;
            }
        }
    }

    async getAvailableTimes(): Promise<void> {
        const dateRequired: string = new DatePipe("en-US").transform(this.orderDate, "yyyy-MM-dd");
        this.loadingTimes = true;
        this.orderTimeIsCarryOver = false;
        try {
            this.availableTimes = await this.orderModel.getAvailableOrderTimes(dateRequired);
            this.loadingTimes = false;
            if (this.orderTime) {
                if (!this.availableTimes.specificTimeGroups.some((group: AvailableOrderTimesGroupData) => {
                    return group.specificTimes.some((time: AvailableOrderTimeData) => {
                        if (this.sameTime(this.orderTime, this.parseTime(time.time)) && time.available) {
                            this.orderTimeIsCarryOver = time.carryOver;
                            return true;
                        }
                        return false;
                    });
                })) {
                    this.orderTime = undefined;
                }
            } else {
                this.availableTimes.specificTimeGroups.some((group: AvailableOrderTimesGroupData) => {
                    return group.specificTimes.some((time: AvailableOrderTimeData) => {
                        if (time.available) {
                            this.orderTime = this.parseTime(time.time);
                            this.orderTimeIsCarryOver = time.carryOver;
                            return true;
                        }
                        return false;
                    });
                });
            }
            this.changeDetectorRef.detectChanges();
        } catch (error) {
            this.availableTimes = null;
            this.loadingTimes = false;
            this.changeDetectorRef.detectChanges();
        }
    }

    formatDate(date: Date): string {
        return new DatePipe("en-US").transform(date, "EEE, MMM d yyyy");
    }

    formatTime(date: Date): string {
        return new DatePipe("en-US").transform(date, "h:mma").toUpperCase();
    }

    parseDate(dateSt: string): Date {
        const parts: string[] = dateSt.split("-");
        let year: number = parseInt(parts[0]);
        let month: number = parseInt(parts[1]);
        const day: number = parseInt(parts[2]);
        const date: Date = this.sessionModel.now;
        date.setFullYear(year, month - 1, day);
        return date;
    }

    parseTime(timeSt: string): Date {
        const parts: string[] = timeSt.split(":");
        let hours: number = parseInt(parts[0]);
        const minutes: number = parseInt(parts[1]);
        if (hours < 12 && timeSt.toLowerCase().endsWith("pm")) {
            hours += 12;
        } else if (hours === 12 && timeSt.toLowerCase().endsWith("am")) {
            hours = 0;
        }
        const date: Date = this.sessionModel.now;
        date.setHours(hours, minutes, 0, 0);
        return date;
    }

    sameTime(time1: Date, time2: Date): boolean {
        return time1.getHours() === time2.getHours() && time1.getMinutes() === time2.getMinutes();
    }

    isToday(date: Date): boolean {
        return this.formatDate(date) === this.formatDate(this.sessionModel.now);
    }

    dateFieldValue(date: Date): string {
        return this.isToday(date) ? "woa.button.order-time.laterToday" : "'" + this.formatDate(date) + "'";
    }

    timeFieldValue(): string {
        return this.loadingTimes ? "woa.button.order-time.loadingTimes"
            : !this.timesAvailable ? "woa.button.order-time.selectTime"
                : this.orderTime ? "'" + this.formatTime(this.orderTime) + (this.orderTimeIsCarryOver ? " *" : "") + "'"
                    : "woa.button.order-time.selectTime";
    }

    timeSlotLabel(time: AvailableOrderTimeData, forList: boolean = false): string {
        return time.available ? "woa.label.order-time.available"
            : time.unavailabilityReason === "closed" ? "woa.label.order-time.closed"
                : time.unavailabilityReason === "closing" ? "woa.label.order-time.closing"
                    : time.unavailabilityReason === "past" ? "woa.label.order-time.past"
                        : time.unavailabilityReason === "capacity" && forList ? "woa.label.order-time.listCapacity"
                            : time.unavailabilityReason === "capacity" ? "woa.label.order-time.capacity"
                                : "";
    }

    showTimeInList(time: AvailableOrderTimeData): boolean {
        return time.available || time.unavailabilityReason === "capacity";
    }

    @HostListener("click", ["$event"])
    onClick(event: MouseEvent) {
        event.stopPropagation();
        this.showDateSelect = false;
        this.showTimeSelect = false;
        this.changeDetectorRef.detectChanges();
    }

    toggleDateSelection(): void {
        this.showDateSelect = !this.showDateSelect;
        if (this.showDateSelect) {
            this.showTimeSelect = false;
        }
        this.changeDetectorRef.detectChanges();
    }

    toggleTimeSelection(): void {
        this.showTimeSelect = !this.showTimeSelect;
        if (this.showTimeSelect) {
            this.showDateSelect = false;
        }
        this.changeDetectorRef.detectChanges();
    }

    selectDate(date: Date): void {
        this.orderDate = date;
        this.showDateSelect = false;
        // noinspection JSIgnoredPromiseFromCall
        this.getAvailableTimes();
        this.changeDetectorRef.detectChanges();
    }

    selectTime(time: AvailableOrderTimeData): void {
        this.orderTime = this.parseTime(time.time);
        this.orderTimeIsCarryOver = time.carryOver;
        this.showTimeSelect = false;
        this.changeDetectorRef.detectChanges();
    }

    confirmTime(): void {
        this.orderDate.setHours(this.orderTime.getHours(), this.orderTime.getMinutes(), 0, 0);
        this.orderingContextModel.expectedCompletion = this.orderDate;
        if (this.orderTimeIsCarryOver) {
            this.orderingContextModel.expectedCompletion.setDate(this.orderingContextModel.expectedCompletion.getDate() + 1);
        }
        // noinspection JSIgnoredPromiseFromCall
        this.navigationService.dismissPage(this, true);
    }

    confirmASAP(): void {
        this.orderingContextModel.expectedCompletion = undefined;
        // noinspection JSIgnoredPromiseFromCall
        this.navigationService.dismissPage(this, true);
    }
}



// WEBPACK FOOTER //
// ./src/app/ordering-context/order-time-selection-page/order-time-selection-page.component.ts